import { ContextType } from '../../../State/Game/IContext';
import { IGameState } from '../../../State/Game/IGameState';
import { FBTrackingEvent } from '../../../Utility/Tracking/FBTrackingEvent';
import BaseUISystem from '../BaseUISystem';
import ThreeStarRewardEffect from '../../../Prototype/Effect/ThreeStarRewardEffect';
import ThreeStarRewardTextEffect from '../../../Prototype/Effect/ThreeStarRewardTextEffect';
import { Random } from '../../../Utility/Random';
import Renderable from '../../../Component/Renderable';
import ElementComponent from '../../../Component/UI/Element';
import Interactable from '../../../Component/Interactable';
import { UIActions } from '../../../State/UI/UIActions';
import {
    GameActions,
    ResponseStatus,
    ResponseType,
} from '../../../State/Game/GameActions';
import Point from '../../../Utility/Point';
import Game from '../../../Game';
import TransformComponent from '../../../Component/Transform';
import { StartAnimation } from '../../Animation/AnimationHelper';
import { RenderSystemEvent } from '../../Render/RenderSystem';
import { PlayerActions } from '../../../State/Player/PlayerActions';
import {
    AdvertisementArea,
    AdvertisementType,
    RewardType,
} from '../../../Utility/SDK/Advertisement';
import { AUDIO_UI_3_STAR_REWARD, AUDIO_UI_BUTTON_DEFAULT } from '../../Audio';
import GameObject from '../../../GameObject/GameObject';
import { DragonbonesAnimationEvent } from '../../../Component/Dragonbones';
import { GameObjectEvent } from '../../../GameObject/GameObjectEvent';
import TextComponent from '../../../Component/Render/Text';
import { StartAudio } from '../../Audio/AudioHelper';
import { AudioTrackType } from '../../Audio/AudioClip';
import { GIFT_REWARDS, GameRewardType } from '../../../Constants/Reward';
import { applyDefaultButtonAnimation } from '../UIHelper';
import BaseMenu from './BaseMenu';

const SUBTITLE: string = 'main_container/subtitle_text';
const INTERACTABLES: string = 'main_container/interactable_container';
const BTN_SHARE: string = 'main_container/interactable_container/share_button';
const TXT_SHARE: string = 'main_container/interactable_container/share_text';
const BTN_OPEN_ALL: string =
    'main_container/interactable_container/open_all_button';
const BTN_CLAIM: string = 'main_container/interactable_container/claim_button';

export default class ThreeStarRewardMenuHandler extends BaseMenu {
    private levelNumber: number;
    private lootBoxes: any[];
    private coins: number;
    private lives: number;

    /**
     * Check if user attempted to watch an ad in this dialog to prevent
     * the previous context response from carrying over from the last time
     * the user watched an ad to open other loot boxes.
     */
    private watchedAd: boolean;

    constructor(uiSystem: BaseUISystem) {
        super('threeStarRewardMenu', uiSystem);
    }

    public handleStateChanged(state: IGameState) {
        // If completed advertisement open other gifts.
        if (
            this.watchedAd &&
            state.context.response &&
            state.context.response.responseType ===
                ResponseType.DISPLAY_ADVERTISEMENT &&
            state.context.response.status === ResponseStatus.SUCCESS &&
            state.context.response.data.payload.g_rewardType ===
                RewardType.THREE_STAR_REWARD
        ) {
            this.openOtherLootBoxes();
        }
    }

    protected onCreate(): void {
        // Button share.
        const btnShare = this.element.find(BTN_SHARE);
        btnShare.on(GameObjectEvent.SELECTED, () => {
            const tick = btnShare.find('tick_icon').getComponent(Renderable);
            tick.visible = !tick.visible;
        });
        applyDefaultButtonAnimation(btnShare, AUDIO_UI_BUTTON_DEFAULT);

        // Button open all.
        const btnOpenAll = this.element.find(BTN_OPEN_ALL);
        btnOpenAll.on(GameObjectEvent.SELECTED, () => {
            this.system.state.dispatch(
                GameActions.setContext(ContextType.DISPLAY_ADVERTISEMENT, {
                    placement: {
                        id: 0 /*changed from Game.config.adRewardedID*/,
                        type: AdvertisementType.REWARDED,
                    },
                    payload: {
                        g_gameArea: AdvertisementArea.THREE_STAR_REWARD_MENU,
                        g_rewardType: RewardType.THREE_STAR_REWARD,
                    },
                })
            );
            this.watchedAd = true;
        });
        applyDefaultButtonAnimation(btnOpenAll, AUDIO_UI_BUTTON_DEFAULT);

        // Button claim.
        const btnClaim = this.element.find(BTN_CLAIM);
        btnClaim.on(GameObjectEvent.SELECTED, () => {
            this.claim();
        });
        applyDefaultButtonAnimation(btnClaim, AUDIO_UI_BUTTON_DEFAULT);
    }

    protected onShow(params: any): void {
        this.watchedAd = false;

        // Store parameters.
        this.levelNumber = params.levelNumber;

        // Reset reward values.
        this.coins = 0;
        this.lives = 0;

        // Create 3 loot boxes.
        const box1 = GameObject.Instantiate(ThreeStarRewardEffect, {
            position: new Point(175, 300),
        });
        box1.addComponent(Interactable);

        const box2 = GameObject.Instantiate(ThreeStarRewardEffect, {
            position: new Point(395, 300),
        });
        box2.addComponent(Interactable);

        const box3 = GameObject.Instantiate(ThreeStarRewardEffect, {
            position: new Point(615, 300),
        });
        box3.addComponent(Interactable);

        this.element.find('main_container').addChild(box1);
        this.element.find('main_container').addChild(box2);
        this.element.find('main_container').addChild(box3);

        this.lootBoxes = [
            { box: box1, open: false },
            { box: box2, open: false },
            { box: box3, open: false },
        ];

        // Start the loot boxes idle animation in different frames.
        for (let i = 0, n = this.lootBoxes.length; i < n; i++) {
            this.lootBoxes[i].box.on(DragonbonesAnimationEvent.COMPLETE, () => {
                this.lootBoxes[i].box.getComponent(
                    Interactable
                ).enabled = false;
            });
            StartAnimation(this.lootBoxes[i].box, 'DragonbonesDefault', {
                animationName: 'idle',
                progress: 1 - 0.33 * i,
            });
        }

        this.element.find(SUBTITLE).getComponent(Renderable).alpha = 1;

        this.element.find(INTERACTABLES).getComponent(Renderable).alpha = 0;
        this.element
            .find(INTERACTABLES)
            .getComponent(Renderable).visible = false;

        if (Game.state.getState().platform.adsRewarded) {
            this.enableAds();
        } else {
            this.disableAds();
        }

        Game.sdk.gameTracking(FBTrackingEvent.three_star_window_show);

        // Hide share button and text if not supported
        const hasShareableMomentsSupport = Game.sdk.driver.hasShareableMomentsSupport();
        this.element
            .find(BTN_SHARE)
            .getComponent(Renderable).visible = hasShareableMomentsSupport;
        this.element
            .find(TXT_SHARE)
            .getComponent(Renderable).visible = hasShareableMomentsSupport;

        // Hide tick icon (share with friends).
        this.system.scene.emit(
            RenderSystemEvent.BRING_TO_FRONT,
            this.element.find(INTERACTABLES)
        );
        this.element
            .find(`${BTN_SHARE}/tick_icon`)
            .getComponent(Renderable).visible = false;
    }

    protected onShown(): void {
        for (let i = 0, n = this.lootBoxes.length; i < n; i++) {
            this.lootBoxes[i].box.on(GameObjectEvent.SELECTED, () => {
                this.openFirstLootBox(i);
            });
        }
    }

    protected onHide(): void {
        for (let i = this.lootBoxes.length - 1; i >= 0; i--) {
            this.lootBoxes[i].box.destroy();
        }

        // Player receive lives and coins.
        if (this.lives > 0) {
            this.system.state.dispatch(PlayerActions.receiveLives(this.lives));
        }
        if (this.coins > 0) {
            this.system.state.dispatch(PlayerActions.receiveCoins(this.coins));
        }
    }

    private openFirstLootBox(index: number): void {
        for (let i = 0, n = this.lootBoxes.length; i < n; i++) {
            this.lootBoxes[i].box.off(GameObjectEvent.SELECTED);
        }
        this.openLootBox(index);

        // Fade out subtitle (Open a present).
        StartAnimation(this.element.find(SUBTITLE), 'Alpha', {
            startAlpha: 1,
            endAlpha: 0,
            duration: 500,
        });

        // Fade in buttons.
        this.element
            .find(INTERACTABLES)
            .getComponent(Renderable).visible = true;
        StartAnimation(this.element.find(INTERACTABLES), 'Alpha', {
            startAlpha: 0,
            endAlpha: 1,
            duration: 500,
        });

        StartAudio(
            this.system.scene,
            AUDIO_UI_3_STAR_REWARD,
            AudioTrackType.SFX
        );
    }

    private openOtherLootBoxes(): void {
        for (let i = 0, n = this.lootBoxes.length; i < n; i++) {
            if (!this.lootBoxes[i].open) {
                this.openLootBox(i);
            }
        }

        this.disableAds();

        // UISystem handles state changes before than AudioSystem, so make sure to play the audio in the next frame.
        // state.context.audioEnabled is still "false" at this moment.
        Game.ticker.setImmediate(() => {
            StartAudio(
                this.system.scene,
                AUDIO_UI_3_STAR_REWARD,
                AudioTrackType.SFX
            );
        });
    }

    private openLootBox(index: number): void {
        this.lootBoxes[index].open = true;

        // Get a random reward.
        const rand = Random.getRandomWeighted(
            GIFT_REWARDS.map(item => item.odds)
        );
        const reward = GIFT_REWARDS[rand];

        const textEffect = GameObject.Instantiate(ThreeStarRewardTextEffect);

        switch (reward.type) {
            case GameRewardType.COINS:
                StartAnimation(
                    this.lootBoxes[index].box,
                    'DragonbonesDefault',
                    {
                        animationName:
                            reward.value < 100 ? 'coins50' : 'coins100',
                    }
                );

                this.coins += reward.value;
                break;
            case GameRewardType.LIVES:
                StartAnimation(
                    this.lootBoxes[index].box,
                    'DragonbonesDefault',
                    {
                        animationName: 'heart01',
                    }
                );

                textEffect.getComponent(TransformComponent).y = -25;
                this.lives += reward.value;
                break;
        }

        textEffect.getComponent(TextComponent).text = `+${reward.value}`;
        this.lootBoxes[index].box.addChild(textEffect);
    }

    private claim(): void {
        const share = this.element
            .find(`${BTN_SHARE}/tick_icon`)
            .getComponent(Renderable).visible;

        if (share) {
            Game.state.dispatch(
                GameActions.setContext(ContextType.SHARE_3_STAR_REWARD, {
                    levelNumber: this.levelNumber,
                })
            );
        } else {
            this.system.state.dispatch(UIActions.hidePopup(this.name));
        }
    }

    /**
     * Show both buttons: Open all and Claim.
     */
    private enableAds(): void {
        const btnOpenAll = this.element.find(BTN_OPEN_ALL);
        btnOpenAll.getComponent(Renderable).visible = true;

        const btnClaim = this.element.find(BTN_CLAIM);
        const btnClaimElement = btnClaim.getComponent(ElementComponent);
        btnClaimElement.anchorTopLeft.x = 0.7;
        btnClaimElement.anchorBottomRight.x = 0.7;
    }

    /**
     * Hide Open All button and center Claim button.
     */
    private disableAds(): void {
        const btnOpenAll = this.element.find(BTN_OPEN_ALL);
        btnOpenAll.getComponent(Renderable).visible = false;

        const btnClaim = this.element.find(BTN_CLAIM);
        const btnClaimElement = btnClaim.getComponent(ElementComponent);
        btnClaimElement.anchorTopLeft.x = 0.5;
        btnClaimElement.anchorBottomRight.x = 0.5;
    }
}
